import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { STORAGE_KEYS } from '../constants/storageKeys';
import '../styles/components/SubscriptionGate.css';

interface SubscriptionGateProps {
  children: React.ReactNode;
}

function isLapsed(subscription: ReturnType<typeof useAuth>['subscription']): boolean {
  if (!subscription) return false; // unknown yet — don't block on a transient fetch failure
  if (subscription.status === 'active') return false;
  if (subscription.status === 'trialing') {
    return !!subscription.trial_ends_at && new Date(subscription.trial_ends_at).getTime() <= Date.now();
  }
  return true; // past_due or canceled
}

// Hard-blocks the app shell when the account's subscription has lapsed
// (trial expired unpaid, or payment failed/canceled) — mirrors the
// ProtectedRoute idiom (full-screen fallback instead of the app), but keyed
// on billing status rather than role.
const SubscriptionGate: React.FC<SubscriptionGateProps> = ({ children }) => {
  const { subscription } = useAuth();
  const [redirecting, setRedirecting] = useState(false);

  if (!isLapsed(subscription)) {
    return <>{children}</>;
  }

  const handleResubscribe = async () => {
    setRedirecting(true);
    try {
      const guestguruToken = localStorage.getItem(STORAGE_KEYS.GG_AUTH_TOKEN);
      const apiKey = import.meta.env.VITE_ANS_WEB_API_KEY || '';
      const baseUrl = process.env.REACT_APP_GG_API_URL || 'http://localhost:3000';
      const response = await fetch(`${baseUrl}/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          Authorization: `Bearer ${guestguruToken}`,
        },
        body: JSON.stringify({ callback_url: window.location.href }),
      });
      const data = await response.json();
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        setRedirecting(false);
      }
    } catch {
      setRedirecting(false);
    }
  };

  const label = subscription?.status === 'past_due' ? 'past due' : subscription?.status === 'canceled' ? 'canceled' : 'trial expired';

  return (
    <div className="subscription-gate">
      <div className="subscription-gate-card">
        <h2>Your subscription is {label}</h2>
        <p>Resubscribe to keep planning — your data is safe and waiting for you.</p>
        <button className="primary-btn" onClick={handleResubscribe} disabled={redirecting}>
          {redirecting ? 'Redirecting…' : 'Resubscribe'}
        </button>
      </div>
    </div>
  );
};

export default SubscriptionGate;
